import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lock, 
  Mail, 
  Briefcase, 
  Trash2, 
  LogOut, 
  Check,
  Phone,
  Building,
  Clock,
  User,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Admin = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [careers, setCareers] = useState([]);
  const [stats, setStats] = useState({ total_contacts: 0, unread_contacts: 0, total_careers: 0, unread_careers: 0 });

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  const verifyToken = async (savedToken) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify?token=${savedToken}`, {
        method: 'POST',
      });
      if (response.ok) {
        setToken(savedToken);
        setIsLoggedIn(true);
        fetchData(savedToken);
      } else {
        localStorage.removeItem('adminToken');
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setToken(data.token);
        setIsLoggedIn(true);
        localStorage.setItem('adminToken', data.token);
        toast.success('Giriş başarılı!');
        fetchData(data.token);
      } else {
        toast.error('Geçersiz kullanıcı adı veya şifre');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout?token=${token}`, { method: 'POST' });
    } catch (error) {}
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setToken('');
    toast.success('Çıkış yapıldı');
  };

  const fetchData = async (authToken) => {
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats?token=${authToken}`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Fetch contacts
      const contactsRes = await fetch(`${API_URL}/api/contacts?token=${authToken}`);
      if (contactsRes.ok) {
        const contactsData = await contactsRes.json();
        setContacts(contactsData);
      }

      // Fetch careers
      const careersRes = await fetch(`${API_URL}/api/careers?token=${authToken}`);
      if (careersRes.ok) {
        const careersData = await careersRes.json();
        setCareers(careersData);
      }
    } catch (error) {
      toast.error('Veri yükleme hatası');
    }
  };

  const markAsRead = async (type, id) => {
    try {
      const endpoint = type === 'contact' ? 'contacts' : 'careers';
      await fetch(`${API_URL}/api/${endpoint}/${id}/read?token=${token}`, { method: 'PUT' });
      fetchData(token);
      toast.success('Okundu olarak işaretlendi');
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;
    try {
      const endpoint = type === 'contact' ? 'contacts' : 'careers';
      await fetch(`${API_URL}/api/${endpoint}/${id}?token=${token}`, { method: 'DELETE' });
      fetchData(token);
      toast.success('Kayıt silindi');
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceLabel = (service) => {
    const labels = {
      'port_agency': 'Liman Acenteliği',
      'cruise_agency': 'Kruvaziyer Acenteliği',
      'yacht_agency': 'Yat Acenteliği',
      'ship_supply': 'Gemi Tedarik'
    };
    return labels[service] || service;
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <main className="min-h-screen bg-navy flex items-center justify-center px-4">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-signal/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-signal" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white">Admin Paneli</h1>
            <p className="text-slate-400 text-sm mt-2">Yönetim paneline erişmek için giriş yapın</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-editorial text-slate-400 block mb-2">Kullanıcı Adı</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-signal transition-colors"
                placeholder="admin"
                required
              />
            </div>
            <div>
              <label className="text-editorial text-slate-400 block mb-2">Şifre</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-signal transition-colors"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-signal flex items-center justify-center gap-2"
            >
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 text-slate-400 hover:text-white text-sm transition-colors"
          >
            ← Ana Sayfaya Dön
          </button>
        </div>
      </main>
    );
  }

  // Admin Dashboard
  return (
    <main className="min-h-screen bg-slate-100 pt-24">
      <div className="container-bespoke py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy">Admin Paneli</h1>
            <p className="text-slate-500">Rota Maritime Agency Yönetim Sistemi</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-slate-600 hover:text-signal transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Çıkış</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{stats.total_contacts}</p>
                <p className="text-sm text-slate-500">Toplam Mesaj</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-signal/10 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-signal" />
              </div>
              <div>
                <p className="text-2xl font-bold text-signal">{stats.unread_contacts}</p>
                <p className="text-sm text-slate-500">Okunmamış</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{stats.total_careers}</p>
                <p className="text-sm text-slate-500">İş Başvurusu</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{stats.unread_careers}</p>
                <p className="text-sm text-slate-500">Yeni Başvuru</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'contacts'
                ? 'bg-navy text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            İletişim Mesajları ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab('careers')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'careers'
                ? 'bg-navy text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Briefcase className="w-4 h-4 inline mr-2" />
            İş Başvuruları ({careers.length})
          </button>
        </div>

        {/* Content */}
        <div className="bg-white shadow-sm">
          {activeTab === 'contacts' && (
            <div className="divide-y divide-slate-100">
              {contacts.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Henüz mesaj yok</p>
                </div>
              ) : (
                contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className={`p-6 ${!contact.is_read ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-navy">{contact.name}</h3>
                          {!contact.is_read && (
                            <span className="text-xs bg-signal text-white px-2 py-1 rounded">YENİ</span>
                          )}
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {getServiceLabel(contact.service_type)}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-3">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            <a href={`mailto:${contact.email}`} className="hover:text-signal">{contact.email}</a>
                          </span>
                          {contact.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <a href={`tel:${contact.phone}`} className="hover:text-signal">{contact.phone}</a>
                            </span>
                          )}
                          {contact.company && (
                            <span className="flex items-center gap-1">
                              <Building className="w-4 h-4" />
                              {contact.company}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(contact.created_at)}
                          </span>
                        </div>
                        <p className="text-slate-700 bg-slate-50 p-4 rounded">{contact.message}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {!contact.is_read && (
                          <button
                            onClick={() => markAsRead('contact', contact.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Okundu işaretle"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteItem('contact', contact.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'careers' && (
            <div className="divide-y divide-slate-100">
              {careers.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Henüz iş başvurusu yok</p>
                </div>
              ) : (
                careers.map((career) => (
                  <div
                    key={career.id}
                    className={`p-6 ${!career.is_read ? 'bg-green-50/50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-navy">{career.name}</h3>
                          {!career.is_read && (
                            <span className="text-xs bg-signal text-white px-2 py-1 rounded">YENİ</span>
                          )}
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            {career.position === 'general_application' ? 'Genel Başvuru' : career.position}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-3">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            <a href={`mailto:${career.email}`} className="hover:text-signal">{career.email}</a>
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${career.phone}`} className="hover:text-signal">{career.phone}</a>
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {career.experience} yıl deneyim
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(career.created_at)}
                          </span>
                        </div>
                        <div className="bg-slate-50 p-4 rounded">
                          <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" /> Ön Yazı
                          </p>
                          <p className="text-slate-700">{career.message}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {!career.is_read && (
                          <button
                            onClick={() => markAsRead('career', career.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Okundu işaretle"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteItem('career', career.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};
